import React from 'react';
import { Typography, Box, Modal } from '@fullstory/components';
import { defineMessage, FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { LibraryObjectResourceType } from '..';
import { intl } from '@fullstory/intl';
import { ItemType } from '../../../types';

export const testIds = {
  DELETE_MODAL: 'libary-object-landing-delete-modal',
};

interface DeleteModalProps extends React.ComponentProps<typeof Modal> {
  items: ItemType[];
  id: string;
  resourceType: LibraryObjectResourceType;
  onConfirm: () => void;
  onCloseSignal: () => void;
}

const deleteModalMessages: {
  [key in LibraryObjectResourceType]: { confirm: string; lastUpdated: MessageDescriptor };
} = {
  Segment: {
    confirm: intl.formatMessage({
      defaultMessage: 'Delete will remove this Segment permanently. How would you like to proceed?',
      id: 'o31dNI',
    }),
    lastUpdated: defineMessage({
      defaultMessage:
        '{lastUpdatedBy} most recently updated this Segment on {lastUpdated, date, ::Mdyy} at {lastUpdated, time, short}.',
      id: 'YSAJUX',
    }),
  },
  Metric: {
    confirm: intl.formatMessage({
      defaultMessage: 'Delete will remove this Metric permanently. How would you like to proceed?',
      id: 'rRPTuE',
    }),
    lastUpdated: defineMessage({
      defaultMessage:
        '{lastUpdatedBy} most recently updated this Metric on {lastUpdated, date, ::Mdyy} at {lastUpdated, time, short}.',
      id: 'Ol6thc',
    }),
  },
  Journey: {
    confirm: intl.formatMessage({
      defaultMessage: 'Delete will remove this Journey permanently. How would you like to proceed?',
      id: 'yt7J6S',
    }),
    lastUpdated: defineMessage({
      defaultMessage:
        '{lastUpdatedBy} most recently updated this Journey on {lastUpdated, date, ::Mdyy} at {lastUpdated, time, short}.',
      id: 'Wuwb+U',
    }),
  },
  Heatmap: {
    confirm: intl.formatMessage({
      defaultMessage: 'Delete will remove this Heatmap permanently. How would you like to proceed?',
      id: 'Hj1yzY',
    }),
    lastUpdated: defineMessage({
      defaultMessage:
        '{lastUpdatedBy} most recently updated this Heatmap on {lastUpdated, date, ::Mdyy} at {lastUpdated, time, short}.',
      id: 'Z+2FkS',
    }),
  },
  Funnel: {
    confirm: intl.formatMessage({
      defaultMessage: 'Delete will remove this Funnel permanently. How would you like to proceed?',
      id: 'HJ1tc9',
    }),
    lastUpdated: defineMessage({
      defaultMessage:
        '{lastUpdatedBy} most recently updated this Funnel on {lastUpdated, date, ::Mdyy} at {lastUpdated, time, short}.',
      id: 'Z3wAES',
    }),
  },
  Dashboard: {
    confirm: intl.formatMessage({
      defaultMessage:
        'Delete will remove this Dashboard permanently. How would you like to proceed?',
      id: 'Or/J57',
    }),
    lastUpdated: defineMessage({
      defaultMessage:
        '{lastUpdatedBy} most recently updated this Dashboard on {lastUpdated, date, ::Mdyy} at {lastUpdated, time, short}.',
      id: 'jyrrQw',
    }),
  },
  Retention: {
    confirm: intl.formatMessage({
      defaultMessage:
        'Delete will remove this Retention permanently. How would you like to proceed?',
      id: 'YJJ7Ra',
    }),
    lastUpdated: defineMessage({
      defaultMessage:
        '{lastUpdatedBy} most recently updated this Retention on {lastUpdated, date, ::Mdyy} at {lastUpdated, time, short}.',
      id: 'cgSZSo',
    }),
  },
  Space: {
    confirm: intl.formatMessage({
      defaultMessage: 'Delete will remove this Space permanently. How would you like to proceed?',
      id: 'KW5BJf',
    }),
    lastUpdated: defineMessage({
      defaultMessage:
        '{lastUpdatedBy} most recently updated this Space on {lastUpdated, date, ::Mdyy} at {lastUpdated, time, short}.',
      id: 'BQAaqY',
    }),
  },
};

export const DeleteModal: React.FC<DeleteModalProps> = ({
  items,
  id,
  resourceType,
  onConfirm,
  onCloseSignal,
}) => {
  const intl = useIntl();

  const item = items.find(item => item.id === id);

  if (!item) return null;

  const lastUpdated = new Date(item.lastUpdated as string);
  const lastUpdatedValid = !isNaN(lastUpdated.getTime()) && item.lastUpdatedBy;

  const messages = deleteModalMessages[resourceType];

  return (
    <Modal
      data-component="DeleteModal"
      onCloseSignal={onCloseSignal}
      size="small"
      testId={testIds.DELETE_MODAL}
    >
      <Modal.Header onCloseSignal={onCloseSignal}>
        <Typography variant="heading1">
          <FormattedMessage
            defaultMessage="Delete: {name}"
            id="Lk8Jz8"
            description="Modal popup header for deleting an item from a list"
            values={{ name: item.name }}
          />
        </Typography>
      </Modal.Header>
      <Modal.Body>
        <Box paddingBottom={12}>
          <Typography variant="body2">{messages.confirm}</Typography>
        </Box>
        {lastUpdatedValid && (
          <Typography variant="body3">
            <FormattedMessage
              {...messages.lastUpdated}
              values={{
                lastUpdatedBy: item.lastUpdatedBy,
                lastUpdated: new Date(item.lastUpdated as string),
              }}
            />
          </Typography>
        )}
      </Modal.Body>
      <Modal.FooterActions
        testId="deleteModal"
        primaryAction={{
          action: onConfirm,
          text: intl.formatMessage({
            defaultMessage: 'Delete',
            id: 'z7a75L',
            description: 'Delete button deletes the item and closes the modal popup',
          }),
        }}
        secondaryAction={{
          action: onCloseSignal,
          text: intl.formatMessage({
            defaultMessage: 'Cancel',
            id: '0gnUW1',
            description: 'Cancel button closes the modal popup',
          }),
        }}
      />
    </Modal>
  );
};
